import { z, type infer as zInfer } from "zod";
import { timestamp } from "@/hooks/firestore/models/timestamp";
import {
  VERIFICATION_STATUSES,
  USER_VERIFICATION_STATUSES,
} from "@/features/account/types/Verification";

const verificationStatusEnum = z.enum(VERIFICATION_STATUSES);

export const user = z.object({
  casinoRecentlyPlayed: z.record(z.string(), timestamp).optional(),
  entryCounts: z
    .object({
      HISTORICAL: z.number().nullish(),
      LIVE: z.number().nullish(),
      UPCOMING: z.number().nullish(),
    })
    .nullish(),
  payId: z
    .object({
      id: z.string(),
    })
    .nullish(),
  notificationsSummary: z
    .object({
      latestCreatedAt: timestamp.nullish(),
      undismissedCount: z.number().nullish(),
    })
    .nullish(),
  profile: z
    .object({
      avatarUri: z.string().nullish(),
      clientNumber: z.number(),
      cooldownEndTime: z.string().optional(),
      country: z.string(),
      createdAt: z.string(),
      dateOfBirth: z.string(),
      email: z.string(),
      emailVerificationStatus: z.enum([
        "VERIFIED",
        "UNVERIFIED",
        "NOT_REQUIRED",
      ]),
      fiatCurrency: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      mfa: z.string(),
      migration: z.number(),
      phone: z.string().optional(),
      postCode: z.string().optional(),
      returningNSER: z.boolean(),
      state: z.string().nullish(),
      status: z.enum(["ACTIVE", "LOCKED", "CLOSED", "DORMANT"]),
      street: z.string(),
      streetNumber: z.string(),
      suburb: z.string(),
      unit: z.string().optional(),
      updatedAt: z.string(),
      userId: z.string(),
      username: z.string(),
      nationality: z.string().optional(),
      verificationStatus: verificationStatusEnum.optional(),
      userVerificationStatus: z.enum(USER_VERIFICATION_STATUSES),
    })
    .nullish(),
  phoneDetails: z
    .object({
      createdAt: z.string(),
      lastUpdatedAt: z.string().optional(),
      phone: z.string(),
      verificationStatus: verificationStatusEnum,
    })
    .optional(),
  promotionCode: z
    .object({
      active: z.boolean(),
      claimerAmount: z.number(),
      code: z.string(),
      currency: z.string(),
      ownerAmount: z.number(),
    })
    .optional(),
  settings: z
    .object({
      acceptOdds: z.enum(["MANUAL", "AUTO", "AUTO_HIGHER"]).nullish(),
      allowCancelWithdrawal: z.boolean().nullish(),
      defaultHub: z.enum(["CASINO", "SPORTS", "ESPORTS", "RACING"]).nullish(),
      id: z.string().nullish(),
      oddsFormat: z.enum(["DECIMAL", "AMERICAN", "FRACTIONAL"]).nullish(),
      userId: z.string().nullish(),
    })
    .nullish(),
});

export type User = zInfer<typeof user>;
